import { useState, useEffect } from 'react';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import ProgressBar from '@cloudscape-design/components/progress-bar';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator, { StatusIndicatorProps } from '@cloudscape-design/components/status-indicator';
import Tabs from '@cloudscape-design/components/tabs';
import { useApi } from '../hooks/useApi';

interface ReviewItem {
  review_id: string;
  document_id: string;
  title: string;
  tier: number;
  content_type: string;
  created_at: string;
}

interface FreshnessData {
  distribution: { excellent_90_100: number; good_70_89: number; fair_50_69: number; poor_0_49: number };
  mean_score: number;
  total_documents: number;
}

export function GovernancePage() {
  const { apiFetch } = useApi();
  const [reviewQueue, setReviewQueue] = useState<ReviewItem[]>([]);
  const [freshness, setFreshness] = useState<FreshnessData | null>(null);
  const [orphanCount, setOrphanCount] = useState(0);
  const [staleCount, setStaleCount] = useState(0);
  const [gaps, setGaps] = useState<{ query: string; count: number }[]>([]);
  const [activeTab, setActiveTab] = useState<'reviews' | 'health' | 'taxonomy' | 'gaps'>('reviews');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [queueData, freshnessData, orphanData, staleData, gapData] = await Promise.all([
        apiFetch<{ queue: ReviewItem[] }>('/reviews/queue'),
        apiFetch<FreshnessData>('/health/freshness'),
        apiFetch<{ count: number }>('/health/orphans'),
        apiFetch<{ count: number }>('/health/stale'),
        apiFetch<{ gaps: { query: string; count: number }[] }>('/search/gaps'),
      ]);
      setReviewQueue(queueData.queue);
      setFreshness(freshnessData);
      setOrphanCount(orphanData.count);
      setStaleCount(staleData.count);
      setGaps(gapData.gaps.slice(0, 10));
    } catch (err) {
      console.error('Failed to load governance data:', err);
    }
  }

  async function handleApprove(reviewId: string) {
    await apiFetch(`/reviews/${reviewId}/approve`, { method: 'POST' });
    setReviewQueue((q) => q.filter((r) => r.review_id !== reviewId));
  }

  async function handleReject(reviewId: string) {
    const comments = prompt('Rejection reason:');
    if (!comments) return;
    await apiFetch(`/reviews/${reviewId}/reject`, { method: 'POST', body: { comments } });
    setReviewQueue((q) => q.filter((r) => r.review_id !== reviewId));
  }

  const reviewsContent =
    reviewQueue.length === 0 ? (
      <Box textAlign="center" color="text-status-inactive" padding={{ vertical: 'xxl' }}>
        No pending reviews 🎉
      </Box>
    ) : (
      <SpaceBetween size="s">
        {reviewQueue.map((r) => (
          <Container
            key={r.review_id}
            header={
              <Header
                variant="h3"
                description={`Tier ${r.tier} · ${r.content_type} · ${new Date(r.created_at).toLocaleDateString()}`}
                actions={
                  <SpaceBetween direction="horizontal" size="xs">
                    <Button variant="normal" onClick={() => handleApprove(r.review_id)}>
                      Approve
                    </Button>
                    <Button variant="normal" onClick={() => handleReject(r.review_id)}>
                      Reject
                    </Button>
                  </SpaceBetween>
                }
              >
                {r.title}
              </Header>
            }
          />
        ))}
      </SpaceBetween>
    );

  const healthContent = freshness ? (
    <Container header={<Header variant="h3">Freshness Distribution ({freshness.total_documents} documents)</Header>}>
      <SpaceBetween size="l">
        <Bar
          label="Excellent (90-100)"
          value={freshness.distribution.excellent_90_100}
          total={freshness.total_documents}
          type="success"
        />
        <Bar label="Good (70-89)" value={freshness.distribution.good_70_89} total={freshness.total_documents} type="info" />
        <Bar
          label="Fair (50-69)"
          value={freshness.distribution.fair_50_69}
          total={freshness.total_documents}
          type="warning"
        />
        <Bar label="Poor (0-49)" value={freshness.distribution.poor_0_49} total={freshness.total_documents} type="error" />
      </SpaceBetween>
    </Container>
  ) : (
    <Box color="text-status-inactive">No freshness data available.</Box>
  );

  const taxonomyContent = (
    <Container>
      <Box color="text-status-inactive">Taxonomy tree browser — loaded from /api/v1/taxonomy</Box>
    </Container>
  );

  const gapsContent = (
    <SpaceBetween size="xs">
      {gaps.map((g, i) => (
        <Container key={i}>
          <SpaceBetween direction="horizontal" size="s" alignItems="center">
            <Box variant="code">"{g.query}"</Box>
            <Badge color="red">{g.count} misses</Badge>
          </SpaceBetween>
        </Container>
      ))}
    </SpaceBetween>
  );

  return (
    <SpaceBetween size="l">
      <Header variant="h1">Governance Dashboard</Header>

      <ColumnLayout columns={4} variant="text-grid">
        <StatCard
          label="Mean Freshness"
          value={`${freshness?.mean_score || 0}/100`}
          type={freshness && freshness.mean_score >= 85 ? 'success' : 'warning'}
        />
        <StatCard
          label="Pending Reviews"
          value={String(reviewQueue.length)}
          type={reviewQueue.length > 5 ? 'error' : 'info'}
        />
        <StatCard label="Orphaned Docs" value={String(orphanCount)} type={orphanCount > 0 ? 'warning' : 'success'} />
        <StatCard label="Stale Docs" value={String(staleCount)} type={staleCount > 0 ? 'warning' : 'success'} />
      </ColumnLayout>

      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => setActiveTab(detail.activeTabId as typeof activeTab)}
        tabs={[
          { id: 'reviews', label: `Reviews (${reviewQueue.length})`, content: reviewsContent },
          { id: 'health', label: 'Corpus Health', content: healthContent },
          { id: 'taxonomy', label: 'Taxonomy', content: taxonomyContent },
          { id: 'gaps', label: `Content Gaps (${gaps.length})`, content: gapsContent },
        ]}
      />
    </SpaceBetween>
  );
}

function StatCard({ label, value, type }: { label: string; value: string; type: StatusIndicatorProps.Type }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <Box variant="h2" padding={{ bottom: 'xxs' }}>
        <StatusIndicator type={type}>{value}</StatusIndicator>
      </Box>
    </div>
  );
}

function Bar({
  label,
  value,
  total,
  type,
}: {
  label: string;
  value: number;
  total: number;
  type: StatusIndicatorProps.Type;
}) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <ProgressBar
      value={pct}
      label={<StatusIndicator type={type}>{label}</StatusIndicator>}
      additionalInfo={`${value} of ${total} documents`}
      ariaLabel={label}
    />
  );
}
